import { GuestIdentity, HotelContactDetails, HotelSpecialRequest } from '@/src/types/hotels'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../../rootReducer'

interface HotelGuestDetailsState {
    rooms: GuestIdentity[]
    contact: HotelContactDetails
    specialRequest: HotelSpecialRequest
}

const emptyGuest = (): GuestIdentity => ({ title: '', firstName: '', lastName: '' })

const initialState: HotelGuestDetailsState = {
    rooms: [],
    contact: { email: '', mobileCountryCode: '', mobile: '' },
    specialRequest: { ids: [], text: '' }
}

// Draft guest form state shared between the itinerary and guest-details pages.
// Values persist across back-navigation; RN parity: stores/guestDetailsStore.ts.
const hotelGuestDetailsSlice = createSlice({
    name: 'hotelGuestDetails',
    initialState,
    reducers: {
        initGuestDetails: (state, action: PayloadAction<number>) => {
            const roomCount = action.payload
            if (state.rooms.length === roomCount) return
            state.rooms = Array.from({ length: roomCount }, (_, i) => state.rooms[i] ?? emptyGuest())
        },
        setRoomGuest: (state, action: PayloadAction<{ index: number; patch: Partial<GuestIdentity> }>) => {
            const { index, patch } = action.payload
            if (!state.rooms[index]) return
            state.rooms[index] = { ...state.rooms[index], ...patch }
        },
        setContact: (state, action: PayloadAction<Partial<HotelContactDetails>>) => {
            state.contact = { ...state.contact, ...action.payload }
        },
        setSpecialRequest: (state, action: PayloadAction<Partial<HotelSpecialRequest>>) => {
            state.specialRequest = { ...state.specialRequest, ...action.payload }
        },
        resetGuestDetails: () => initialState
    }
})

export const { initGuestDetails, setRoomGuest, setContact, setSpecialRequest, resetGuestDetails } =
    hotelGuestDetailsSlice.actions

export const selectGuestRooms = (state: RootState) => state.hotelGuestDetails.rooms
export const selectGuestContact = (state: RootState) => state.hotelGuestDetails.contact
export const selectSpecialRequest = (state: RootState) => state.hotelGuestDetails.specialRequest

export default hotelGuestDetailsSlice.reducer
